import { motion } from "framer-motion";
import { useMemo, useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
import FooterColumn from "@/components/ui/footer-column";
import { Button } from "@/components/ui/button";
import { Calendar, Users, MessageSquare, Shield, Sparkles, Coffee, TrendingUp, MapPin, Clock } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PromptAndPlateNotifyForm } from "@/components/PromptAndPlateNotifyForm";
import { EventsListingSection } from "@/components/EventsListingSection";
import { PromptAndPlateEvent, ApiEventItem, transformApiEventToEvent } from "@/data/promptAndPlateEvents";
import { getPublicImage } from "@/utils/imageUtils";
import promptPlateHeroBg from "@/assets/prompt-plate-hero-bg.jpg";
import networkingImage from "@/assets/prompt-plate-networking.jpg";
import discussionImage from "@/assets/prompt-plate-discussion.jpg";
const PromptAndPlatePage = () => {
  const [nextSession, setNextSession] = useState<PromptAndPlateEvent | null>(null);
  const [followingSession, setFollowingSession] = useState<PromptAndPlateEvent | null>(null);

  // Memoize fallbackMetadata to prevent infinite re-renders
  const fallbackMetadata = useMemo(() => ({
    title: "Prompt & Plate - AI Lunch for Marketing Leaders | gimmefy",
    description: "Join senior marketers for a casual Friday lunch discussing AI in marketing. No panels, no pitches - just honest conversation over lunch at Cross Street.",
    keywords: "AI marketing lunch, marketing networking, senior marketers, AI discussion, marketing leaders, Singapore marketing events"
  }), []);

  // Fetch events for top section
  useEffect(() => {
    const fetchTopEvents = async () => {
      try {
        // API URL setup
        let apiUrl = "https://api.gimmefy.ai/v2";
        const baseUrl = window.location.origin;
        if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
          apiUrl = "https://apidev.gimmefy.ai/v2";
        } else if (baseUrl.includes("staging.gimmefy.ai")) {
          apiUrl = "https://apistaging.gimmefy.ai/v2";
        }

        const contentUrl = `${apiUrl}/contents/public?filters[group_name]=WEBSITE_PROMPT_AND_PLATE&sort=display_order`;
        const response = await fetch(contentUrl);
        const data = await response.json();

        if (data.success && Array.isArray(data.payload)) {
          // Find next_session and following_session
          const nextSessionItem = data.payload.find((item: ApiEventItem) => item.tags === "next_session");
          const followingSessionItem = data.payload.find((item: ApiEventItem) => item.tags === "following_session");

          if (nextSessionItem) {
            setNextSession(transformApiEventToEvent(nextSessionItem, getPublicImage));
          }
          if (followingSessionItem) {
            setFollowingSession(transformApiEventToEvent(followingSessionItem, getPublicImage));
          }
        }
      } catch (error) {
        console.error("Error fetching top events:", error);
      }
    };

    fetchTopEvents();
  }, []);

  const {
    seoData,
    canonicalUrl,
    noindex,
    nofollow
  } = useSEO({
    fallbackMetadata
  });
  return <div className="min-h-screen bg-background">
      {seoData && <SEO data={seoData} canonicalUrl={canonicalUrl} noindex={noindex} nofollow={nofollow} />}
      <Navigation />

      {/* Hero Section - Full Width with Image */}
      <section className="relative pt-20 pb-24 overflow-hidden full-bleed">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img src={promptPlateHeroBg} alt="Marketing professionals enjoying lunch conversation" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-slate-900/90 to-black/95" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(168,85,247,0.12),transparent_50%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />
        </div>

        <div className="container mx-auto max-w-6xl relative z-10 px-6 sm:px-8 md:px-12 lg:px-16">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 items-center lg:items-start">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="text-center lg:text-left flex flex-col items-center lg:items-start w-full max-w-lg lg:max-w-none">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/30 mb-6 backdrop-blur-sm shadow-lg">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white tracking-wide">Strict No-Pitch Event</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight whitespace-nowrap">
                <span className="text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.9)] [text-shadow:_0_4px_8px_rgba(0,0,0,0.8)]">Prompt & </span>
                <span className="bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300 bg-clip-text text-transparent drop-shadow-[0_2px_20px_rgba(59,130,246,0.8)] [text-shadow:_0_4px_12px_rgba(59,130,246,0.6)]">Plate</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white/95 mb-4 font-light tracking-wide drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
                Where AI meets authentic conversation
              </p>
              
              <p className="text-lg text-white/90 mb-8 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">A casual lunch or drinks session, on us, for senior marketers. Talk AI. Meet interesting people. No panels, no pitches—just honest insights over great food.</p>

              <div className="flex flex-col gap-3 sm:gap-4 w-auto sm:w-full sm:max-w-md mx-auto sm:mx-0">
                <Button size="lg" variant="hero" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-[0_10px_40px_-10px_hsl(var(--primary)_/_0.4)] hover:shadow-[0_15px_50px_-10px_hsl(var(--primary)_/_0.5)] hover:scale-105 transition-all duration-300 sm:w-full" onClick={() => document.getElementById('events-listing')?.scrollIntoView({
                behavior: 'smooth'
              })}>
                  Request your seat
                </Button>
                
                <Button size="lg" variant="minimal" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 backdrop-blur-sm bg-card/60 border-2 hover:bg-card/80 hover:border-primary/40 hover:scale-105 transition-all duration-300 sm:w-full" onClick={() => document.getElementById('notify-form')?.scrollIntoView({
                behavior: 'smooth'
              })}>Keep me updated</Button>
              </div>
            </motion.div>

            <motion.div className="hidden lg:block lg:mt-12" initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }}>
              <div className="space-y-4 sm:space-y-6">
                {nextSession && <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-4 sm:p-6 shadow-xl">
                    <div className="flex items-center gap-3 mb-3 sm:mb-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-primary uppercase tracking-wide">
                        Next Session
                      </span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold mb-2">{nextSession.day}, {nextSession.date}</p>
                    <div className="flex items-center gap-2 text-base sm:text-lg text-muted-foreground mb-1">
                      <Clock className="w-4 h-4" />
                      <span>{nextSession.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-base sm:text-lg text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{nextSession.location}</span>
                    </div>
                    <div className="text-sm text-muted-foreground pl-6">
                      {nextSession.venue}
                    </div>
                  </div>}
                {followingSession && <div className="hidden sm:block bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-4 sm:p-6 shadow-xl">
                    <div className="flex items-center gap-3 mb-3 sm:mb-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-primary uppercase tracking-wide">
                        Following Session
                      </span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold mb-2">{followingSession.day}, {followingSession.date}</p>
                    <div className="flex items-center gap-2 text-base sm:text-lg text-muted-foreground mb-1">
                      <Clock className="w-4 h-4" />
                      <span>{followingSession.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-base sm:text-lg text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{followingSession.location}</span>
                    </div>
                    <div className="text-sm text-muted-foreground pl-6">
                      {followingSession.venue}
                    </div>
                  </div>}
                
                <p className="text-sm text-white/90 text-center drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                  Exact venue will be shared with attendees prior to the event.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What This Is - Image Left, Text Right */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <img src={networkingImage} alt="Marketing professionals networking over lunch" className="rounded-2xl shadow-xl" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 mb-4 backdrop-blur-sm">
                <Coffee className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 tracking-wide">The Concept</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">What this is</h2>
              <p className="text-lg text-foreground/80 leading-relaxed mb-6">Prompt and Plate is an intentionally relaxed, small table setting where the conversation is driven by who is at the table, not a programme.</p>
              <p className="text-lg text-foreground/80 leading-relaxed">
                Bring curiosity, a short case or a question about AI in marketing, and leave with fresh thinking and one or two new contacts you actually want to follow up with. <span className="font-semibold text-foreground">We cover lunch, you bring the judgement.</span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Details - Modern Cards */}
      <section className="py-20 px-4 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 mb-4 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-semibold text-purple-600 dark:text-purple-400 tracking-wide">Quick details</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Everything you need to know</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0
          }} className="group bg-card border border-border/50 rounded-xl p-6 hover:shadow-xl hover:border-blue-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 hidden sm:flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                <Calendar className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">When</h3>
              <p className="text-foreground/70 leading-relaxed">Check out our events listings for the next meetup</p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group bg-card border border-border/50 rounded-xl p-6 hover:shadow-xl hover:border-emerald-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 hidden sm:flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <Users className="w-6 h-6 text-emerald-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Who</h3>
              <p className="text-foreground/70 leading-relaxed">Senior marketers, Heads, Directors, CMOs and founders with marketing remit</p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group bg-card border border-border/50 rounded-xl p-6 hover:shadow-xl hover:border-purple-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-purple-500/10 hidden sm:flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors">
                <MessageSquare className="w-6 h-6 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Format</h3>
              <p className="text-foreground/70 leading-relaxed">Casual table conversation, no set agenda, no recordings, no pitches</p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group bg-card border border-border/50 rounded-xl p-6 hover:shadow-xl hover:border-amber-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 hidden sm:flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                <Shield className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Policy</h3>
              <p className="text-foreground/70 leading-relaxed">Come to meet, listen and share; keep it candid and commercial free</p>
            </motion.div>
          </div>

          {/* CTA Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mt-12">
            <Button size="lg" variant="hero" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-[0_10px_40px_-10px_hsl(var(--primary)_/_0.4)] hover:shadow-[0_15px_50px_-10px_hsl(var(--primary)_/_0.5)] hover:scale-105 transition-all duration-300" onClick={() => document.getElementById('events-listing')?.scrollIntoView({
            behavior: 'smooth'
          })}>
              Request Your Seat
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Curation */}
      <section className="py-20 px-4 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <TrendingUp className="w-5 h-5 text-primary" />
                <span className="text-sm font-semibold text-primary">Curated Selection</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">How we curate</h2>
              
              <p className="text-lg text-foreground/90 leading-relaxed mb-4">
                We prioritise <span className="font-bold text-foreground bg-gradient-to-r from-amber-500/10 to-orange-500/10 px-2 py-0.5 rounded">seniority, role fit and the practical value</span> you will add to the conversation.
              </p>
              
              <p className="text-lg text-foreground/90 leading-relaxed mb-8">
                Each table mixes verticals and functions so the discussion stays lively and useful. Vendor seats are rare and always disclosed up front.
              </p>
              
              <Button size="lg" variant="hero" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-[0_10px_40px_-10px_hsl(var(--primary)_/_0.4)] hover:shadow-[0_15px_50px_-10px_hsl(var(--primary)_/_0.5)] hover:scale-105 transition-all duration-300 w-full" onClick={() => document.getElementById('notify-form')?.scrollIntoView({
              behavior: 'smooth'
            })}>
                Can't make this one?
              </Button>
            </motion.div>

            {/* Right Column - Image */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <img src={discussionImage} alt="Marketing professionals having a lively discussion over lunch" className="rounded-2xl shadow-xl w-full h-auto object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Events Listing Section */}
      <EventsListingSection />

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">The Process</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">What happens next?</h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-gradient-to-br from-card to-card/90 backdrop-blur-md border-2 border-emerald-500/20 rounded-3xl p-8 shadow-[0_20px_60px_-20px_rgba(16,185,129,0.25)] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-cyan-500/5" />
            <div className="relative z-10 space-y-6">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold shadow-lg">1</div>
                <p className="text-lg text-foreground/90 leading-relaxed">We review requests for fit and confirm seats <span className="font-bold text-foreground">within 72 hours</span></p>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold shadow-lg">2</div>
                <p className="text-lg text-foreground/90 leading-relaxed">Confirmed guests receive a calendar invite and venue details <span className="font-bold text-foreground">five days prior</span></p>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold shadow-lg">3</div>
                <p className="text-lg text-foreground/90 leading-relaxed">A short reminder arrives <span className="font-bold text-foreground">three days prior</span></p>
              </div>
              <div className="pt-4 border-t border-emerald-500/20">
                <p className="text-foreground/70 italic">Need to cancel? Reply to your confirmation and we'll release the seat to the waitlist.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 text-center tracking-tight">
            Frequently asked questions
          </motion.h2>
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <Accordion type="single" collapsible className="w-full space-y-4">
              <AccordionItem value="item-1" className="bg-gradient-to-br from-card to-card/90 backdrop-blur-sm border-2 border-border/50 rounded-2xl px-6 shadow-[0_8px_30px_-10px_hsl(var(--foreground)_/_0.08)] hover:border-blue-500/30 hover:shadow-[0_12px_40px_-10px_rgba(59,130,246,0.15)] transition-all duration-300">
                <AccordionTrigger className="text-left text-lg font-bold hover:no-underline tracking-tight py-6">
                  Is there a fee?
                </AccordionTrigger>
                <AccordionContent className="text-foreground/85 text-base leading-relaxed pb-6">
                  No, it's on us. But if something comes up and If you cannot attend, please cancel promptly so another senior marketer can take the seat.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-gradient-to-br from-card to-card/90 backdrop-blur-sm border-2 border-border/50 rounded-2xl px-6 shadow-[0_8px_30px_-10px_hsl(var(--foreground)_/_0.08)] hover:border-emerald-500/30 hover:shadow-[0_12px_40px_-10px_rgba(16,185,129,0.15)] transition-all duration-300">
                <AccordionTrigger className="text-left text-lg font-bold hover:no-underline tracking-tight py-6">
                  How many people will be there?
                </AccordionTrigger>
                <AccordionContent className="text-foreground/85 text-base leading-relaxed pb-6">
                  Expect about a dozen senior marketers. We keep a small buffer to allow for last minute changes while preserving the intimate feel.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-gradient-to-br from-card to-card/90 backdrop-blur-sm border-2 border-border/50 rounded-2xl px-6 shadow-[0_8px_30px_-10px_hsl(var(--foreground)_/_0.08)] hover:border-purple-500/30 hover:shadow-[0_12px_40px_-10px_rgba(168,85,247,0.15)] transition-all duration-300">
                <AccordionTrigger className="text-left text-lg font-bold hover:no-underline tracking-tight py-6">
                  Will there be speakers or presentations?
                </AccordionTrigger>
                <AccordionContent className="text-foreground/85 text-base leading-relaxed pb-6">
                  No. This is a casual table, conversation led. If someone wants to share a quick one to two minute story, that is fine, there is no formal presenting.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-gradient-to-br from-card to-card/90 backdrop-blur-sm border-2 border-border/50 rounded-2xl px-6 shadow-[0_8px_30px_-10px_hsl(var(--foreground)_/_0.08)] hover:border-amber-500/30 hover:shadow-[0_12px_40px_-10px_rgba(245,158,11,0.15)] transition-all duration-300">
                <AccordionTrigger className="text-left text-lg font-bold hover:no-underline tracking-tight py-6">
                  Can I record or share the discussion?
                </AccordionTrigger>
                <AccordionContent className="text-foreground/85 text-base leading-relaxed pb-6">
                  Sessions are unrecorded to encourage candidness. We'll probably tag you in a LinkedIn post but whatever happens at the table, stays at the table.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-gradient-to-br from-card to-card/90 backdrop-blur-sm border-2 border-border/50 rounded-2xl px-6 shadow-[0_8px_30px_-10px_hsl(var(--foreground)_/_0.08)] hover:border-cyan-500/30 hover:shadow-[0_12px_40px_-10px_rgba(6,182,212,0.15)] transition-all duration-300">
                <AccordionTrigger className="text-left text-lg font-bold hover:no-underline tracking-tight py-6">
                  I am not strictly marketing, can I attend?
                </AccordionTrigger>
                <AccordionContent className="text-foreground/85 text-base leading-relaxed pb-6">
                  We prioritise marketing leaders but will consider related roles if they clearly add to the conversation.
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* CTA Button */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mt-12">
              <Button size="lg" variant="hero" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-[0_10px_40px_-10px_hsl(var(--primary)_/_0.4)] hover:shadow-[0_15px_50px_-10px_hsl(var(--primary)_/_0.5)] hover:scale-105 transition-all duration-300" onClick={() => document.getElementById('events-listing')?.scrollIntoView({
              behavior: 'smooth'
            })}>
                Request Your Seat
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Notify Me Form */}
      <section id="notify-form" className="py-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Can't make the session?</h2>
              <p className="text-xl text-muted-foreground">If the next event is full or you want to be informed when upcoming sessions are scheduled, please use the form below and we will email you when the next session date is announced.</p>
            </div>
            <PromptAndPlateNotifyForm />
          </motion.div>
        </div>
      </section>

      <FooterColumn />
    </div>;
};
export default PromptAndPlatePage;